/**
 * 매장별 허용 결제 수단 조합 할당 조회 훅 (#217/#218, β-2b).
 *
 * admin `StoreResource::accepted_payment_methods` 는 `api.v1.stores.show` 전용 응답
 * (목록/embed 경로에서는 의도적 제외 — N+1 방지). 따라서 메뉴 API 응답을 재활용할 수 없고
 * CheckoutPage 에서 별도 `storeApi.getStore` 호출 필요.
 *
 * β-1b `usePaymentMethodCombinations` 패턴을 따라 모듈 수준 캐시 + retry + test 리셋 export.
 * 매장 assignment 는 런타임 중 파트너가 PATCH 할 가능성 있어 per-mount fetch 허용하되
 * 동일 storeId 는 세션 캐시.
 *
 * Staleness 방어 (#306 항목 3, menu 세션 10):
 *   - **TTL 5분**: 캐시 엔트리는 생성 후 5분 경과 시 stale — 훅 재마운트 시 refetch
 *   - **visibilitychange**: 탭이 다시 visible 이 될 때 전체 캐시 stale 마킹 →
 *     파트너가 admin 탭에서 accepted_payment_methods PATCH 후 menu 탭으로 돌아오는
 *     시나리오에서 즉시 refetch 유도
 */
import { useCallback, useEffect, useState } from 'react';
import { useStorePricing } from '@/contexts/StorePricingContext';
import { storeApi } from '@/lib/api';
import type { StoreDetail, StorePaymentMethodAssignment } from '@/lib/api/types';
import { logger } from '@/utils/logger';

/**
 * TTL. 파트너 PATCH 빈도 낮음 + 평균 체류시간 기반으로 5분 선택.
 * 더 공격적으로 가면 네트워크 비용 증가, 더 보수적으로 가면 stale 윈도우 확대.
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  store: StoreDetail;
  cachedAt: number;
}

/**
 * storeId → entry 모듈 수준 캐시. TTL 만료 또는 visibilitychange 시 stale 간주.
 */
let cachedByStoreId: Map<string, CacheEntry> = new Map();
let inflightByStoreId: Map<string, Promise<StoreDetail>> =
  new Map();

/**
 * visibilitychange 이벤트 기반 invalidation nonce + generation.
 * - `visibilityNonce`: 훅의 `useEffect` deps 에 참여, mounted hook 재평가 유도
 * - `cacheGeneration`: 진행 중 fetch 가 완료 시점에 "emit 이후 구세대" 인지
 *   판별하여 stale-write (구 데이터를 새 cachedAt 으로 재기록) 방지
 */
let visibilityNonce = 0;
let cacheGeneration = 0;
type Listener = () => void;
const nonceListeners = new Set<Listener>();

function subscribeToVisibilityInvalidation(listener: Listener): () => void {
  nonceListeners.add(listener);
  return () => {
    nonceListeners.delete(listener);
  };
}

function emitVisibilityInvalidation(): void {
  visibilityNonce += 1;
  cacheGeneration += 1;
  // 캐시 전체 clear — 새 마운트가 fresh cache 로 착각하지 않도록.
  cachedByStoreId.clear();
  // inflight 도 clear — 다음 마운트는 brand new fetch 시작. 기존 promise 는
  // 계속 실행되지만 완료 시 generation mismatch 로 cache write skip.
  inflightByStoreId.clear();
  nonceListeners.forEach((l) => l());
}

let visibilityListenerRegistered = false;
// named handler — reset 시 removeEventListener 로 정확히 제거 가능
const visibilityChangeHandler = (): void => {
  if (
    typeof document !== 'undefined' &&
    document.visibilityState === 'visible'
  ) {
    emitVisibilityInvalidation();
  }
};

function ensureVisibilityListener(): void {
  if (visibilityListenerRegistered) return;
  if (typeof document === 'undefined') return; // SSR / test non-jsdom 가드
  document.addEventListener('visibilitychange', visibilityChangeHandler);
  visibilityListenerRegistered = true;
}

function isFresh(entry: CacheEntry | undefined, now: number): boolean {
  if (!entry) return false;
  return now - entry.cachedAt < CACHE_TTL_MS;
}

function getAssignments(store: StoreDetail): StorePaymentMethodAssignment[] {
  return store.accepted_payment_methods ?? [];
}

function getTaxRateRatio(store: StoreDetail): number | null {
  return store.tax_rate != null ? store.tax_rate / 100 : null;
}

interface UseStoreAcceptedPaymentMethodsReturn {
  assignments: StorePaymentMethodAssignment[];
  platformCardPrepayAvailability: 'unavailable' | 'unknown';
  loading: boolean;
  error: Error | null;
  /** 에러 후 재시도 트리거. 캐시 무효화 + useEffect 재실행. */
  retry: () => void;
}

export function useStoreAcceptedPaymentMethods(
  storeId: string | number | null,
): UseStoreAcceptedPaymentMethodsReturn {
  const { setStorePricing } = useStorePricing();
  const cacheKey = storeId !== null ? String(storeId) : '';
  const freshCached = (() => {
    if (!cacheKey) return null;
    const entry = cachedByStoreId.get(cacheKey);
    return isFresh(entry, Date.now()) ? getAssignments(entry!.store) : null;
  })();

  const [assignments, setAssignments] = useState<StorePaymentMethodAssignment[]>(
    freshCached ?? [],
  );
  const [loading, setLoading] = useState(
    cacheKey !== '' && freshCached === null,
  );
  const [error, setError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // visibilityNonce 변경에 반응하여 재평가 — 전역 invalidation 이 발화하면
  // useEffect deps 가 바뀌어 freshness 재검사 경로 진입.
  const [localVisibilityNonce, setLocalVisibilityNonce] =
    useState(visibilityNonce);

  useEffect(() => {
    ensureVisibilityListener();
    const unsubscribe = subscribeToVisibilityInvalidation(() => {
      setLocalVisibilityNonce(visibilityNonce);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (cacheKey === '') {
      setAssignments([]);
      setLoading(false);
      setError(null);

      return;
    }

    const entry = cachedByStoreId.get(cacheKey);
    if (isFresh(entry, Date.now())) {
      const store = entry!.store;
      setStorePricing(getTaxRateRatio(store), store.currency, store.points ?? null);
      setAssignments(getAssignments(store));
      setLoading(false);
      setError(null);

      return;
    }

    // stale 또는 미캐시 — 재fetch.
    setLoading(true);
    let cancelled = false;
    // fetch 시작 시점 generation 스냅샷 — 완료 시점 current generation 과 다르면
    // 도중에 visibility invalidation 이 발화된 것이므로 cache 에 쓰면 stale-write.
    const fetchGeneration = cacheGeneration;

    const existing = inflightByStoreId.get(cacheKey);
    const promise =
      existing ??
      (() => {
        // One owner per physical request: cancelled subscribers must not
        // duplicate failure capture or remove a replacement request.
        const p = storeApi
          .getStore(cacheKey)
          .then((response) => {
            const store = response.data;
            // Exercise the same extraction contract before publishing a cache
            // entry; malformed responses remain one observable request failure.
            getAssignments(store);
            getTaxRateRatio(store);
            if (fetchGeneration === cacheGeneration && inflightByStoreId.get(cacheKey) === p) {
              cachedByStoreId.set(cacheKey, { store, cachedAt: Date.now() });
            }
            return store;
          })
          .catch((err: unknown) => {
            logger.error('[store-accepted-payment-methods] 조회 실패', err);
            throw err;
          })
          .finally(() => {
            if (inflightByStoreId.get(cacheKey) === p) inflightByStoreId.delete(cacheKey);
          });
        inflightByStoreId.set(cacheKey, p);

        return p;
      })();

    const handleError = (err: unknown) => {
      if (!cancelled) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    };
    promise
      .then((store) => {
        const data = getAssignments(store);
        if (!cancelled) {
          // 메뉴 목록 응답은 points 를 생략할 수 있지만 store 상세 응답에는 포함된다.
          // checkout 이 이미 이 상세 응답을 조회하므로 포인트 정책도 함께 동기화한다.
          setStorePricing(getTaxRateRatio(store), store.currency, store.points ?? null);
          setAssignments(data);
          setLoading(false);
          setError(null);
        }
      }, handleError)
      .catch((err: unknown) => {
        // Errors in an individual pricing callback are distinct from the
        // shared request rejection, which its owner already recorded once.
        logger.error('[store-accepted-payment-methods] 조회 실패', err);
        handleError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, retryNonce, localVisibilityNonce, setStorePricing]);

  const retry = useCallback(() => {
    if (cacheKey !== '') {
      cachedByStoreId.delete(cacheKey);
    }
    setError(null);
    setLoading(true);
    setRetryNonce((n) => n + 1);
  }, [cacheKey]);

  const entry = cachedByStoreId.get(cacheKey);
  // Detail metadata is authoritative only for the same store and fresh cache generation.
  const platformCardPrepayAvailability = isFresh(entry, Date.now()) &&
    String(entry!.store.id) === cacheKey &&
    entry!.store.platform_card_prepay_availability === 'unavailable'
    ? 'unavailable' : 'unknown';
  return { assignments, platformCardPrepayAvailability, loading, error, retry };
}

/**
 * @internal Vitest 전용. 프로덕션 번들에서는 `undefined` 로 치환.
 */
export const __resetStoreAcceptedPaymentMethodsCacheForTesting:
  | (() => void)
  | undefined =
  import.meta.env.MODE === 'test'
    ? () => {
        cachedByStoreId = new Map();
        inflightByStoreId = new Map();
        visibilityNonce = 0;
        cacheGeneration = 0;
        nonceListeners.clear();
        // 실제 document 리스너 제거 + flag 리셋 — HMR / 테스트 격리
        if (visibilityListenerRegistered && typeof document !== 'undefined') {
          document.removeEventListener(
            'visibilitychange',
            visibilityChangeHandler,
          );
        }
        visibilityListenerRegistered = false;
      }
    : undefined;

/**
 * @internal Vitest 전용. 테스트에서 visibilitychange 시뮬레이션용.
 * `document.visibilityState = 'visible'` 전환 시 발화되는 경로와 동일.
 */
export const __emitVisibilityInvalidationForTesting:
  | (() => void)
  | undefined =
  import.meta.env.MODE === 'test' ? emitVisibilityInvalidation : undefined;
